import type { CurrencyEnum } from '@pco/api/generated/graphql';
import { Box } from '@pco/atoms/Box';
import { Separator } from '@pco/atoms/Separator';
import { Typography } from '@pco/atoms/Typography';
import { formatCurrency } from '@pco/formatting/number';
import { weights } from '@pco/theme/defaultTypography';
import type { Nullish } from '@pco/types';
import styled from 'styled-components';

const ContainerBox = styled(Box)<{ $hidden?: boolean }>`
	${({ $hidden }) => $hidden && 'display: none;'}
`;

const CurrentPrice = styled(Typography)<ProductPriceProps>`
  ${({ theme: { typography }, isLight }) => {
		return `
			${isLight ? (isLight === 1 ? typography.body12 : typography.body14) : typography.body16}
			${weights.semibold}
			white-space: nowrap;
			// TODO: add weights to the theme and use them from there.
		`;
	}}
`;

const OriginalPrice = styled(Typography)`
  ${({ theme: { typography } }) => {
		return `
			${typography.body12}
			${weights.book}
			text-decoration: line-through;
			white-space: nowrap;
		`;
	}}
`;

export interface ProductPriceProps {
	price?: Nullish<number>;
	msrp?: Nullish<number>;
	currency?: Nullish<CurrencyEnum>;
	isNational?: boolean;
	isLight?: boolean | number; // Add a prop to control the wight scheme of the price tag.
	column?: boolean; // Add a prop to control whether the price tag should be displayed in a column.
	showSeparator?: boolean; // Prop used to prevent displaying on order detail.
}

export const ProductPrice: React.FC<ProductPriceProps> = ({
	price,
	msrp,
	currency,
	isNational,
	isLight,
	column,
	showSeparator = true,
}) => {
	if (msrp == null) return null;

	const shouldShowDealerPrice = price != null && price !== msrp;

	const dealerPrice =
		shouldShowDealerPrice && formatCurrency(price, currency ?? 'USD');
	const formattedMsrp = `MSRP ${formatCurrency(msrp, currency ?? 'USD')}`;
	const formattedPrice = formatCurrency(msrp, currency ?? 'USD');

	const showSecondPrice = price && price < msrp && showSeparator;
	const isPriceLowerThanMsrp = price != null && price < msrp;

	const isHidden = price === 0;

	if (isNational) {
		return (
			<Box
				gap="8px"
				alignItems={(column && 'flex-start') || 'center'}
				flexDirection={(column && 'column') || 'row'}
			>
				<CurrentPrice isLight={isLight}>{formattedMsrp}</CurrentPrice>
			</Box>
		);
	}

	return (
		<ContainerBox
			gap="8px"
			alignItems={(column && 'flex-end') || 'center'}
			flexDirection={(column && 'column') || 'row'}
			$hidden={isHidden}
		>
			{dealerPrice && (
				<Box gap="8px" alignItems="center">
					<CurrentPrice isLight={isLight}>{dealerPrice}</CurrentPrice>
					{!column && showSecondPrice && (
						<Separator orientation="vertical" _height="15px" />
					)}
				</Box>
			)}
			{dealerPrice && isPriceLowerThanMsrp && (
				<OriginalPrice>{formattedMsrp}</OriginalPrice>
			)}
			{!dealerPrice && (
				<CurrentPrice isLight={isLight}>{formattedPrice}</CurrentPrice>
			)}
		</ContainerBox>
	);
};
